import { useState, useEffect } from 'react';
import { Switch, Text, TextInput, Button, Card, Group, Space, Alert } from '@mantine/core';
import { IconInfoCircle } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import SettingsApi from '../../api/SettingsApi';
import DNSInstructions from './DNSInstructions';
import { showWpSyncWarning, HelpTip } from '../GeneralComponent/GeneralComponent';

interface AdvancedTrackingConfigProps {
    websiteId: string;
    websiteUrl?: string;
    onConfigChange?: () => void;
}

export function AdvancedTrackingConfig({ websiteId, websiteUrl, onConfigChange }: AdvancedTrackingConfigProps) {
    const [enabled, setEnabled] = useState(false);
    const [subdomain, setSubdomain] = useState('landing-page');
    const [loading, setLoading] = useState(false);
    const [configData, setConfigData] = useState<any>(null);
    const [showDNS, setShowDNS] = useState(false);
    const [disconnecting, setDisconnecting] = useState(false);
    const [hasPermission, setHasPermission] = useState(true);
    const [isLoading, setIsLoading] = useState(true);
    const [isAlreadyActive, setIsAlreadyActive] = useState(false);
    const [originServer, setShowOriginServer] = useState("");


    useEffect(() => {
        loadConfig();
    }, [websiteId]);

    const loadConfig = (showLoading = true) => {
        if (showLoading) setIsLoading(true);
        SettingsApi.getcustomhostnameconfig({ website_id: websiteId })
            .then(res => res.json())
            .then(data => {
                if (showLoading) setIsLoading(false);
                if (data.status === 1) {
                    const config = data.data;

                    // Check permission status from API response
                    const permissionGranted = config.has_permission === true;
                    setHasPermission(permissionGranted);
                    setShowOriginServer(config.origin_server);

                    // Only process config data if user has permission
                    // When permission is denied, backend returns minimal response
                    if (permissionGranted) {
                        const hasCustomHostname = config.custom_hostname_id != null && config.custom_hostname != null;

                        // Toggle ON if advanced_tracking_enabled OR custom hostname exists
                        const isEnabled = config.advanced_tracking_enabled === 1 || hasCustomHostname;

                        setEnabled(isEnabled);
                        setSubdomain(config.custom_subdomain || 'landing-page');
                        setConfigData(config);

                        // Show DNS instructions if custom hostname exists (even if not yet active)
                        setShowDNS(hasCustomHostname);
                    }
                }
            })
            .catch(err => {
                if (showLoading) setIsLoading(false);
                console.error('Failed to load config:', err);
            });
    };


    const handleDisconnect = () => {
        if (!window.confirm('Are you sure you want to disconnect Advanced Tracking? Your tracking will switch back to the default domain and you will need to remove the DNS records from your domain registrar.')) {
            return;
        }

        setDisconnecting(true);
        SettingsApi.deletecustomhostname({ website_id: websiteId })
            .then(res => res.json())
            .then(data => {
                setDisconnecting(false);
                if (data.status === 1) {
                    notifications.show({
                        title: 'Success',
                        message: data.message,
                        color: 'green'
                    });
                    showWpSyncWarning(data, websiteId);

                    // Reset UI state
                    setEnabled(false);
                    setShowDNS(false);
                    setConfigData(null);
                    setSubdomain('landing-page');

                    // Reload config and refresh tracking code (without hiding component)
                    loadConfig(false);
                    if (onConfigChange) onConfigChange();
                } else {
                    // Check if permission denied
                    if (data.data && data.data.permission_denied) {
                        setHasPermission(false);
                    }
                    notifications.show({
                        title: 'Error',
                        message: data.message,
                        color: 'red'
                    });
                }
            })
            .catch(err => {
                setDisconnecting(false);
                notifications.show({
                    title: 'Error',
                    message: 'Network error occurred',
                    color: 'red'
                });
            });
    };

    const handleSave = () => {
        if (!subdomain.trim()) {
            notifications.show({
                title: 'Error',
                message: 'Please enter a subdomain',
                color: 'red'
            });
            return;
        }

        setLoading(true);
        SettingsApi.createcustomhostname({
            website_id: websiteId,
            subdomain: subdomain.trim()
        })
        .then(res => res.json())
        .then(data => {
            setLoading(false);
            if (data.status === 1) {
                const isDuplicateHostname = data.data.is_duplicate === true;
                setIsAlreadyActive(data.data?.custom_hostname_status === 'active');

                notifications.show({
                    title: isDuplicateHostname ? 'Info' : 'Success',
                    message: data.message,
                    color: isDuplicateHostname ? 'blue' : 'green'
                });
                setConfigData(data.data);
                setShowDNS(true);
                // Do NOT call loadConfig() here — the save response already
                // contains all TXT data. Calling loadConfig() would set isLoading=true
                // (hiding the component) and overwrite configData with an async response.
                if (onConfigChange) onConfigChange();
            } else {
                console.error('handleSave - Error response:', data.message);
                // Check if permission denied
                if (data.data && data.data.permission_denied) {
                    setHasPermission(false);
                }
                notifications.show({
                    title: 'Error',
                    message: data.message,
                    color: 'red'
                });
            }
        })
        .catch(err => {
            console.error('handleSave - Network error:', err);
            setLoading(false);
            notifications.show({
                title: 'Error',
                message: 'Network error occurred',
                color: 'red'
            });
        });
    };

    const extractRootDomain = (url: string): string => {
        try {
            let domain = url.replace(/^https?:\/\//, '').replace(/^www\./, '').split('/')[0].replace(/:\d+$/, '');
            const parts = domain.split('.');
            if (parts.length <= 2) return domain;

            // Common two-part TLDs where the root domain has 3 parts
            const twoPartTlds = [
                'co.uk','com.au','co.nz','co.za','com.br','co.in','org.uk','net.au',
                'ac.uk','gov.uk','org.au','co.jp','or.jp','ne.jp','co.kr','com.sg',
                'com.hk','com.tw','co.il','com.mx','com.ar','co.th','com.ph','com.my',
                'com.pk','com.ng','com.eg','co.ke','com.ua','com.tr','com.sa','com.pe',
                'com.co','com.ve','com.ec','com.uy','com.py','com.bo','com.gt','com.sv',
                'com.hn','com.ni','com.cr','com.pa','com.do','com.cu','com.pr'
            ];
            const lastTwo = parts.slice(-2).join('.');
            if (twoPartTlds.includes(lastTwo) && parts.length >= 3) {
                return parts.slice(-3).join('.');
            }
            return parts.slice(-2).join('.');
        } catch {
            return url;
        }
    };

    const rootDomain = extractRootDomain(websiteUrl);
    const fullHostname = subdomain + '.' + rootDomain;
    const isActive = configData?.custom_hostname_status === 'active';

    // Don't render anything while loading or if user doesn't have permission
    // This completely hides the Advanced Tracking section for unauthorized users
    if (isLoading || !hasPermission) {
        return null;
    }

    return (
        <>
            <Card withBorder p="md" mt="md" mb="xl">
                <Group spacing="xs" mb="md">
                    <Text size="md" fw={600}>Advanced (First-Party S2S)</Text>
                    <HelpTip helpId={373} />
                </Group>

                <Text size="sm" color="dimmed" mb="md">
                    Use your own custom subdomain for tracking scripts instead of {originServer}
                </Text>

            <Group spacing="xs" mb="md">
                <Text size="sm" fw={500}>Enable Advanced (First-Party S2S)</Text>
                <Switch
                    checked={enabled}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => {
                        // Prevent manual toggle OFF if custom hostname exists
                        if (showDNS && e.currentTarget.checked === false) {
                            notifications.show({
                                title: 'Info',
                                message: 'Please use "Disconnect Advanced Tracking" button to disable this feature',
                                color: 'blue'
                            });
                            return;
                        }
                        setEnabled(e.currentTarget.checked);
                    }}
                    size="md"
                    onLabel="ON"
                    offLabel="OFF"
                />
            </Group>

            {enabled && (
                <>
                    

                    <Text size="sm" fw={500} mb={4}>Tracking Subdomain</Text>
                    <Text size="xs" color="dimmed" mb="sm">
                        Enter the subdomain prefix for your custom tracking domain
                    </Text>

                    <Group spacing={0} style={{ maxWidth: '400px' }}>
                        <TextInput
                            value={subdomain}
                            onChange={(e) => {
                                const value = e.currentTarget.value;
                                const valid = /^[a-zA-Z0-9-]*$/.test(value);
                                if (!valid) {
                                    alert("Only letters, numbers, and hyphens (-) are allowed in the subdomain.");
                                    return;
                                }
                                setSubdomain(value);
                            }}
                            onClick={(e) => e.stopPropagation()}
                            placeholder="landing-page"
                            disabled={showDNS}
                            readOnly={showDNS}
                            w={160}
                            styles={{
                                input: {
                                    borderTopRightRadius: 0,
                                    borderBottomRightRadius: 0,
                                    textAlign: 'center',
                                    fontWeight: 500
                                }
                            }}
                        />
                        <div style={{
                            padding: '7px 14px',
                            backgroundColor: '#e9ecef',
                            border: '1px solid #ced4da',
                            borderLeft: 'none',
                            borderTopRightRadius: '4px',
                            borderBottomRightRadius: '4px',
                            fontSize: '14px',
                            fontWeight: 500,
                            color: '#495057',
                            whiteSpace: 'nowrap'
                        }}>
                            .{rootDomain}
                        </div>
                    </Group>

                    <Space h="md" />

                    {!showDNS ? (
                        <>
                            <Alert icon={<IconInfoCircle size={16} />} color="blue" variant="light" style={{ whiteSpace: 'nowrap', width: 'fit-content' }}>
                                Your custom tracking domain will be: <strong>{fullHostname}</strong>
                            </Alert>

                            <Space h="md" />

                            <Button
                                onClick={(e) => { e.stopPropagation(); handleSave(); }}
                                loading={loading}
                                disabled={!subdomain.trim()}
                            >
                                Connect
                            </Button>
                        </>
                    ) : (
                        <>
                            <Alert icon={<IconInfoCircle size={16} />} color={isActive ? 'green' : 'blue'} variant="light" style={{ whiteSpace: 'nowrap', width: 'fit-content' }}>
                                {isActive
                                    ? <>Custom tracking domain configured <strong>{configData?.custom_hostname || fullHostname}</strong></>
                                    : <>Your custom tracking domain will be <strong>{configData?.custom_hostname || fullHostname}</strong></>
                                }
                            </Alert>

                            {isActive && (
                                <Alert icon={<IconInfoCircle size={16} />} color="teal" variant="light" mt="sm">
                                    <Text size="sm" style={{ lineHeight: 1.5 }}>
                                        <b>Important:</b> Please copy the Universal Smart Code below and add it to your pages to enable advanced tracking with your custom domain.
                                    </Text>
                                </Alert>
                            )}
                        </>
                    )}

                    <Space h="md" />

                    {showDNS && configData && (
                        <DNSInstructions
                            configData={configData}
                            websiteId={websiteId}
                            onVerificationSuccess={() => loadConfig(false)}
                            onConfigChange={onConfigChange}
                            onDisconnect={handleDisconnect}
                            disconnecting={disconnecting}
                            isAlreadyActive={isAlreadyActive}
                        />
                    )}
                </>
            )}
            </Card>
        </>
    );
}

export default AdvancedTrackingConfig;
